import React from "react";
import "./Diagrams.css";

const MONO = "JetBrains Mono, monospace";
const DISPLAY = "Space Grotesk, Inter, sans-serif";
const LINE = "rgba(255,255,255,0.18)";
const ACC = "var(--accent)";
const ACC_SOFT = "rgba(93,242,164,0.5)";
const CY = "rgba(79,211,255,0.7)";

const Label = ({ x, y, children, fill = "rgba(255,255,255,0.55)", size = 9, anchor = "middle", weight }) => (
  <text x={x} y={y} fontFamily={MONO} fontSize={size} fill={fill} textAnchor={anchor} letterSpacing="0.8" fontWeight={weight}>
    {children}
  </text>
);

const Title = ({ x, y, children, size = 13, anchor = "middle", fill = "#eef2f0" }) => (
  <text x={x} y={y} fontFamily={DISPLAY} fontSize={size} fontWeight="600" fill={fill} textAnchor={anchor}>
    {children}
  </text>
);



/* ================================================================== */
/* Layout helpers: every node exposes exact edge anchors, and every     */
/* connector runs from a right-edge anchor to a left-edge anchor over   */
/* a vertical "bus" with rounded corners, so lines always meet boxes.   */
/* ================================================================== */
const node = (x, y, w, h) => ({ x, y, w, h, cx: x + w / 2, cy: y + h / 2, l: [x, y + h / 2], r: [x + w, y + h / 2] });

/** Orthogonal path from point a to point b through vertical bus at x=bx. */
function route(a, b, bx, r = 8) {
  const [x1, y1] = a;
  const [x2, y2] = b;
  if (Math.abs(y1 - y2) < 0.5) return `M${x1} ${y1} H${x2}`;
  const dir = y2 > y1 ? 1 : -1;
  return [
    `M${x1} ${y1}`,
    `H${bx - r}`,
    `Q${bx} ${y1} ${bx} ${y1 + dir * r}`,
    `V${y2 - dir * r}`,
    `Q${bx} ${y2} ${bx + r} ${y2}`,
    `H${x2}`,
  ].join(" ");
}

const MONO_W = (chars, size, ls = 0.5) => chars * (size * 0.6 + ls);

function Defs({ prefix }) {
  const marker = (id, color) => (
    <marker id={`${prefix}-${id}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
      <path d="M0 0L10 5 0 10z" fill={color} />
    </marker>
  );
  return (
    <defs>
      {marker("arr-acc", ACC)}
      {marker("arr-cy", "rgba(79,211,255,0.9)")}
      {marker("arr-amb", "var(--amber)")}
      {marker("arr-dim", "rgba(255,255,255,0.35)")}
      <filter id={`${prefix}-glow`} x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="0" stdDeviation="8" floodColor="#5df2a4" floodOpacity="0.28" />
      </filter>
    </defs>
  );
}

function Wire({ id, d, color = ACC, marker, dots = 1, dur = 3, begin = 0, dashed = false, dim = false }) {
  return (
    <>
      <path
        id={id}
        d={d}
        fill="none"
        stroke={dim ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.3)"}
        strokeWidth="1.3"
        strokeDasharray={dashed ? "4 5" : undefined}
        markerEnd={marker ? `url(#${marker})` : undefined}
      />
      {Array.from({ length: dots }, (_, i) => (
        <circle key={i} r="3.2" fill={color}>
          <animateMotion dur={`${dur}s`} repeatCount="indefinite" begin={`${begin + (i * dur) / dots}s`}>
            <mpath href={`#${id}`} />
          </animateMotion>
        </circle>
      ))}
    </>
  );
}

function Pill({ x, y, text, size = 8, color = "rgba(255,255,255,0.7)" }) {
  const w = MONO_W(text.length, size, 0.4) + 16;
  const h = 18;
  return (
    <g transform={`translate(${x - w / 2} ${y - h / 2})`}>
      <rect width={w} height={h} rx="9" fill="#0f1317" stroke={LINE} />
      <text x={w / 2} y={h / 2} dominantBaseline="central" textAnchor="middle" fontFamily={MONO} fontSize={size} fill={color} letterSpacing="0.4">
        {text}
      </text>
    </g>
  );
}

function Chip({ x, y, text, w, h = 24, size = 8.5 }) {
  const width = w || MONO_W(text.length, size) + 16;
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect width={width} height={h} rx="6" fill="rgba(0,0,0,0.35)" stroke={ACC_SOFT} />
      <text x={width / 2} y={h / 2} dominantBaseline="central" textAnchor="middle" fontFamily={MONO} fontSize={size} fill={ACC} letterSpacing="0.5">
        {text}
      </text>
    </g>
  );
}

function Panel({ n, accent = false, dashed = false, glow, children }) {
  return (
    <g>
      <rect
        x={n.x}
        y={n.y}
        width={n.w}
        height={n.h}
        rx="12"
        fill={accent ? "rgba(93,242,164,0.07)" : "rgba(255,255,255,0.035)"}
        stroke={accent ? ACC_SOFT : LINE}
        strokeWidth={accent ? 1.5 : 1}
        strokeDasharray={dashed ? "5 4" : undefined}
        filter={accent && glow ? `url(#${glow})` : undefined}
      />
      {children}
    </g>
  );
}

function CamNode({ n, label, dim = false }) {
  return (
    <g opacity={dim ? 0.55 : 1}>
      <rect x={n.x} y={n.y} width={n.w} height={n.h} rx="10" fill="rgba(255,255,255,0.035)" stroke={LINE} />
      <rect x={n.x + 14} y={n.cy - 5.5} width="16" height="11" rx="2" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" />
      <path d={`M${n.x + 30} ${n.cy - 2.5}l6-3v11l-6-3z`} fill="rgba(255,255,255,0.7)" />
      <text x={n.x + 44} y={n.cy} dominantBaseline="central" fontFamily={MONO} fontSize="9" fill="rgba(255,255,255,0.85)" letterSpacing="0.5">
        {label}
      </text>
    </g>
  );
}

function OutNode({ n, label, dim = false }) {
  return (
    <g opacity={dim ? 0.55 : 1}>
      <rect x={n.x} y={n.y} width={n.w} height={n.h} rx="10" fill="rgba(255,255,255,0.035)" stroke={LINE} />
      <text x={n.cx} y={n.cy} dominantBaseline="central" textAnchor="middle" fontFamily={MONO} fontSize="9" fill="rgba(255,255,255,0.85)" letterSpacing="0.4">
        {label}
      </text>
    </g>
  );
}

const Header = ({ x, y, children, anchor = "middle", color = "rgba(255,255,255,0.45)" }) => (
  <text x={x} y={y} fontFamily={MONO} fontSize="9" fill={color} textAnchor={anchor} letterSpacing="2.2">
    {children}
  </text>
);

const StatusRow = ({ x, y, text, dotColor = ACC, textColor = "rgba(255,255,255,0.8)" }) => (
  <g>
    <circle cx={x} cy={y} r="3" fill={dotColor} />
    <text x={x + 10} y={y} dominantBaseline="central" fontFamily={MONO} fontSize="8.5" fill={textColor} letterSpacing="0.5">
      {text}
    </text>
  </g>
);

/* ------------------------------------------------------------------ */
export function PlatformDiagram() {
  const P = "pd";
  const cams = ["CAM-01 · Dock", "CAM-02 · Line 2", "CAM-03 · Gate", "CAM-04 · Yard"];
  const outs = ["Dashboards", "Alerts", "API & webhooks", "Reports & agents"];
  const camN = cams.map((_, i) => node(40, 72 + i * 84, 170, 52));
  const outN = outs.map((_, i) => node(920, 72 + i * 84, 120, 52));
  const edge = node(290, 125, 210, 190);
  const cloud = node(610, 125, 230, 190);
  const busIn = 250;
  const busOut = 880;
  const upY = 204;
  const downY = 236;

  return (
    <figure className="diagram card">
      <span className="diagram__hint mono" aria-hidden="true">swipe to explore →</span>
      <div className="diagram__inner">
        <svg viewBox="0 0 1060 440" role="img" aria-labelledby={`${P}-title`}>
          <title id={`${P}-title`}>
            System map: cameras feed an edge node on site, which exchanges events and model updates with the cloud control plane that drives dashboards, alerts, the API and reports.
          </title>
          <Defs prefix={P} />

          <Header x={40} y={38} anchor="start">INPUTS</Header>
          <Header x={edge.cx} y={38}>EDGE · ON SITE</Header>
          <Header x={cloud.cx} y={38}>CLOUD</Header>
          <Header x={outN[0].cx} y={38}>OUTPUTS</Header>

          {/* inputs -> edge */}
          {camN.map((n, i) => (
            <Wire key={i} id={`${P}-in-${i}`} d={route(n.r, edge.l, busIn)} color={CY} dots={1} dur={3.2} begin={i * 0.8} />
          ))}
          <path d={`M${busIn} ${edge.cy} H${edge.x}`} stroke="rgba(255,255,255,0.3)" strokeWidth="1.3" markerEnd={`url(#${P}-arr-cy)`} />
          {camN.map((n, i) => (
            <CamNode key={i} n={n} label={cams[i]} />
          ))}

          {/* edge node */}
          <Panel n={edge} accent glow={`${P}-glow`}>
            <Title x={edge.cx} y={edge.y + 38} size={15}>Edge node</Title>
            <Label x={edge.cx} y={edge.y + 58} size={8.5} fill="rgba(255,255,255,0.6)">JETSON ORIN NX · TENSORRT</Label>
            <Chip x={edge.x + 10} y={edge.y + 72} w={58} text="detect" />
            <Chip x={edge.x + 76} y={edge.y + 72} w={58} text="track" />
            <Chip x={edge.x + 142} y={edge.y + 72} w={58} text="zones" />
            <line x1={edge.x + 12} y1={edge.y + 114} x2={edge.x + edge.w - 12} y2={edge.y + 114} stroke={LINE} />
            <StatusRow x={edge.x + 18} y={edge.y + 136} text="online · 4 streams · 18 ms" />
            <text x={edge.x + 12} y={edge.y + 160} dominantBaseline="central" fontFamily={MONO} fontSize="8" fill="rgba(255,255,255,0.45)" letterSpacing="0.5">
              demo figures · works offline
            </text>
          </Panel>

          {/* edge <-> cloud */}
          <Wire id={`${P}-up`} d={`M${edge.r[0]} ${upY} H${cloud.x}`} marker={`${P}-arr-acc`} dots={2} dur={2.4} />
          <Pill x={(edge.r[0] + cloud.x) / 2} y={upY - 16} text="events · clips" color={ACC} />
          <Wire id={`${P}-down`} d={`M${cloud.x} ${downY} H${edge.r[0]}`} marker={`${P}-arr-amb`} color="var(--amber)" dots={1} dur={3.2} />
          <Pill x={(edge.r[0] + cloud.x) / 2} y={downY + 16} text="models · rules" color="var(--amber)" />

          {/* cloud control plane */}
          <Panel n={cloud}>
            <path
              d={`M${cloud.x + 22} ${cloud.y + 34}a7 7 0 0 1-1-13.9A10 10 0 0 1 ${cloud.x + 40} ${cloud.y + 22}a7.5 7.5 0 0 1-1 12z`}
              fill="rgba(79,211,255,0.12)"
              stroke="rgba(79,211,255,0.8)"
              strokeWidth="1.3"
            />
            <Title x={cloud.x + 50} y={cloud.y + 38} size={15} anchor="start">Control plane</Title>
            <Label x={cloud.x + 16} y={cloud.y + 58} size={8.5} anchor="start" fill="rgba(255,255,255,0.6)">FLEET · MODELS · RETRAINING</Label>
            <Chip x={cloud.x + 10} y={cloud.y + 72} text="dashboards" />
            <Chip x={cloud.x + 90} y={cloud.y + 72} text="llm / vlm" />
            <Chip x={cloud.x + 164} y={cloud.y + 72} text="agents" />
            <line x1={cloud.x + 12} y1={cloud.y + 114} x2={cloud.x + cloud.w - 12} y2={cloud.y + 114} stroke={LINE} />
            <StatusRow x={cloud.x + 18} y={cloud.y + 136} text="12 sites · 3 models · ota updates" dotColor={CY} />
            <text x={cloud.x + 12} y={cloud.y + 160} dominantBaseline="central" fontFamily={MONO} fontSize="8" fill="rgba(255,255,255,0.45)" letterSpacing="0.5">
              EU region · runs in your tenant
            </text>
          </Panel>

          {/* cloud -> outputs */}
          {outN.map((n, i) => (
            <Wire key={i} id={`${P}-out-${i}`} d={route(cloud.r, n.l, busOut)} marker={`${P}-arr-acc`} dots={1} dur={3} begin={i * 0.6} />
          ))}
          {outN.map((n, i) => (
            <OutNode key={i} n={n} label={outs[i]} />
          ))}
        </svg>
      </div>
      <figcaption className="mono">
        System map · edge inference stays on site, the cloud keeps every site consistent
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
export function RetrofitDiagram() {
  const P = "rd";
  const cams = ["CAM-01", "CAM-02", "CAM-03"];
  const mk = (cy) => cams.map((_, i) => node(40, cy - 50 + i * 36, 120, 28));
  const bus = 200;

  // lane 1: today
  const c1 = 112;
  const cam1 = mk(c1);
  const vms1 = node(240, c1 - 28, 200, 56);
  const mon1 = node(500, c1 - 28, 320, 56);

  // lane 2: with Episcope
  const c2 = 330;
  const cam2 = mk(c2);
  const vms2 = node(240, c2 - 28, 200, 56);
  const ai = node(490, c2 - 44, 240, 88);
  const outs = ["Alerts in seconds", "Tickets & reports", "Live dashboards"];
  const outN = outs.map((_, i) => node(830, c2 - 62 + i * 45, 150, 34));
  const busOut = 780;

  return (
    <figure className="diagram card">
      <span className="diagram__hint mono" aria-hidden="true">swipe to explore →</span>
      <div className="diagram__inner">
        <svg viewBox="0 0 1060 440" role="img" aria-labelledby={`${P}-title`}>
          <title id={`${P}-title`}>
            Before and after: today the same cameras record into a video management system that someone has to watch. With Episcope, an AI module is added behind the recorder and produces alerts, tickets and dashboards.
          </title>
          <Defs prefix={P} />

          {/* lane 1 */}
          <Header x={40} y={40} anchor="start">TODAY</Header>
          <g opacity="0.55">
            {cam1.map((n, i) => (
              <Wire key={i} id={`${P}-c1-${i}`} d={route(n.r, vms1.l, bus)} color="rgba(255,255,255,0.5)" dots={1} dur={4} begin={i * 1.1} dim />
            ))}
            <path d={`M${bus} ${vms1.cy} H${vms1.x}`} stroke="rgba(255,255,255,0.22)" strokeWidth="1.3" markerEnd={`url(#${P}-arr-dim)`} />
            <Wire id={`${P}-v1`} d={`M${vms1.r[0]} ${vms1.cy} H${mon1.x}`} marker={`${P}-arr-dim`} color="rgba(255,255,255,0.5)" dots={1} dur={3} dim />
          </g>
          {cam1.map((n, i) => (
            <CamNode key={i} n={n} label={cams[i]} dim />
          ))}
          <g opacity="0.55">
            <Panel n={vms1}>
              <Title x={vms1.cx} y={vms1.y + 24} size={14}>NVR / VMS</Title>
              <Label x={vms1.cx} y={vms1.y + 43} size={8.5}>records everything</Label>
            </Panel>
            <Panel n={mon1} dashed>
              <Title x={mon1.cx} y={mon1.y + 24} size={13}>One person, twelve screens</Title>
              <Label x={mon1.cx} y={mon1.y + 43} size={8.5}>incidents found hours later, if at all</Label>
            </Panel>
          </g>

          <line x1="40" y1="212" x2="1020" y2="212" stroke={LINE} strokeDasharray="3 6" />

          {/* lane 2 */}
          <Header x={40} y={252} anchor="start" color={ACC}>WITH EPISCOPE</Header>
          {cam2.map((n, i) => (
            <Wire key={i} id={`${P}-c2-${i}`} d={route(n.r, vms2.l, bus)} color={CY} dots={1} dur={3} begin={i * 0.9} />
          ))}
          <path d={`M${bus} ${vms2.cy} H${vms2.x}`} stroke="rgba(255,255,255,0.3)" strokeWidth="1.3" markerEnd={`url(#${P}-arr-cy)`} />
          {cam2.map((n, i) => (
            <CamNode key={i} n={n} label={cams[i]} />
          ))}
          <Panel n={vms2}>
            <Title x={vms2.cx} y={vms2.y + 24} size={14}>NVR / VMS</Title>
            <Label x={vms2.cx} y={vms2.y + 43} size={8.5}>kept as is · RTSP · ONVIF · API</Label>
          </Panel>
          <Wire id={`${P}-v2`} d={`M${vms2.r[0]} ${vms2.cy} H${ai.x}`} marker={`${P}-arr-cy`} color={CY} dots={2} dur={1.8} />
          <Panel n={ai} accent glow={`${P}-glow`}>
            <Title x={ai.cx} y={ai.y + 30} size={15} fill={ACC}>AI module</Title>
            <Label x={ai.cx} y={ai.y + 51} size={8.5} fill="rgba(255,255,255,0.6)">JETSON · HAILO · X86 + GPU</Label>
            <Label x={ai.cx} y={ai.y + 71} size={8.5} fill="rgba(255,255,255,0.85)">detect · track · rules · agents</Label>
          </Panel>
          {outN.map((n, i) => (
            <Wire key={i} id={`${P}-o-${i}`} d={route(ai.r, n.l, busOut)} marker={`${P}-arr-acc`} dots={1} dur={2.6} begin={i * 0.7} />
          ))}
          {outN.map((n, i) => (
            <OutNode key={i} n={n} label={outs[i]} />
          ))}

          <Label x={530} y={420} size={8.5} fill="rgba(255,255,255,0.45)">same cameras · same recorder · new capability in weeks, not quarters</Label>
        </svg>
      </div>
      <figcaption className="mono">Retrofit path · nothing on the wall is replaced</figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
export function BuildLoopDiagram() {
  const steps = ["Collect data", "Label", "Train", "Evaluate", "Deploy", "Monitor"];
  const cx = 300;
  const cy = 170;
  const r = 118;
  const pts = steps.map((_, i) => {
    const a = (-90 + i * 60) * (Math.PI / 180);
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  });
  return (
    <figure className="diagram card">
      <div className="diagram__inner diagram__inner--narrow">
        <svg viewBox="0 0 600 340" role="img" aria-labelledby="bl-title">
          <title id="bl-title">A continuous loop: collect data, label, train, evaluate, deploy, monitor, and back to collecting data, all on your hardware.</title>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.14)" strokeDasharray="4 6" className="diagram__orbit" />
          <circle r="3.5" fill={ACC}>
            <animateMotion dur="9s" repeatCount="indefinite" path={`M ${cx} ${cy - r} a ${r} ${r} 0 1 1 -0.01 0`} />
          </circle>
          {pts.map(([x, y], i) => (
            <g key={steps[i]} transform={`translate(${x} ${y})`}>
              <circle r="30" fill="#0f1317" stroke={i === 4 ? ACC_SOFT : LINE} strokeWidth={i === 4 ? 1.5 : 1} />
              <Label x="0" y="-6" size={7.5} fill={ACC}>0{i + 1}</Label>
              <Label x="0" y="9" size={8} fill="rgba(255,255,255,0.85)">{steps[i].toUpperCase()}</Label>
            </g>
          ))}
          <Title x={cx} y={cy - 6} size={14}>Your data.</Title>
          <Title x={cx} y={cy + 14} size={14}>Your hardware.</Title>
          <Label x={cx} y={cy + 34} size={8}>models improve every cycle</Label>
        </svg>
      </div>
      <figcaption className="mono">Build loop · every phase ends with something you can evaluate</figcaption>
    </figure>
  );
}
